import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SendHorizonal } from 'lucide-react';
import { Reveal, Eyebrow } from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { id: 'weekly', label: 'Weekly Dispatches' },
  { id: 'major', label: 'Major Drops Only' },
  { id: 'research', label: 'Research Whitepapers' },
];

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email, frequency });
      toast.success('Signal received. Welcome to the dispatches.', {
        description: `${email} · ${FREQUENCIES.find(f => f.id === frequency).label}`,
      });
      setEmail('');
    } catch (err) {
      toast.error('Transmission failed', { description: 'The studio node did not respond. Try again shortly.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="signal" className="relative py-28 lg:py-40" data-testid="newsletter-section">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <Reveal>
          <Eyebrow>The Library · Transmission Station</Eyebrow>
          <h2 className="font-celestial text-3xl sm:text-4xl lg:text-5xl font-light text-slate-100 leading-tight">
            Join the <span className="text-cyan-300 text-glow-cyan">dispatches</span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl mx-auto">
            Every book, app, paper and thought thread is indexed in the Library — and dispatches carry the best of it to your inbox.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="mt-12 rounded-2xl border border-cyan-500/20 bg-slate-950/70 backdrop-blur-2xl p-7 sm:p-9 text-left shadow-[0_8px_40px_rgba(0,0,0,0.5)]" data-testid="newsletter-form">
            <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">
              &gt; select frequency
            </p>
            <div className="flex flex-wrap gap-2.5 mb-7">
              {FREQUENCIES.map((f) => (
                <button
                  type="button"
                  key={f.id}
                  onClick={() => setFrequency(f.id)}
                  className={`font-mono-precision text-[11px] tracking-[0.15em] px-4 py-2 rounded-full border transition-all duration-300 ${
                    frequency === f.id
                      ? 'border-cyan-400/70 text-cyan-300 bg-cyan-400/10'
                      : 'border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/25'
                  }`}
                  data-testid={`newsletter-frequency-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">
              &gt; enter coordinates
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.earth"
                className="flex-1 bg-[#02040a]/80 border border-white/10 rounded-xl px-5 py-4 font-mono-precision text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 focus:shadow-[0_0_20px_rgba(34,211,238,0.15)] transition-all duration-300"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2.5 bg-cyan-400 text-[#02040a] font-mono-precision text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-xl hover:bg-cyan-300 transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] disabled:opacity-50"
                data-testid="newsletter-submit-button"
              >
                <SendHorizonal size={15} />
                {sending ? 'Transmitting…' : 'Transmit'}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
